import { BaseUITools } from "@/uiControls/base/baseUITools";
import { createNumericSliderUI, NumericSliderUI } from "@/uiControls/base/ui/numericSliderUI";
import { NumericInputUI } from "../base/ui/numericInputUI";
import { createVector3ControlUI } from "../base/components/vector3ControlUI";
import { createViewPortUI } from "../base/components/viewPortUI";
import { CameraController } from "@/visionaryControllers/cameraController";
import { cameraUtils } from "@/utils/cameraUtils";
import { globalConfig, globalManager } from "@/utils/globalConfig";

// 模型辅助工具，扩展多视窗，与Scene无绑定关系，单独使用
export class CameraPanelTools extends BaseUITools {

    private isActive: boolean = false; // 是否激活
    private cameraController: CameraController | null = null; // 相机控制器引用
    private isUpdatingFromCamera: boolean = false; // 防止循环更新标志

    // UI控件引用
    private positionControl: any = null;
    private fovControl: NumericSliderUI | null = null;
    private nearPlaneControl: NumericInputUI | null = null;
    private farPlaneControl: NumericInputUI | null = null;
    private speedControl: NumericSliderUI | null = null;

    constructor(id: string) {
        super(id, false);
        this.init();
    }

    init() {
        const panelContainerElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-container',
        });
        const titleElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title',
        });
        const titleLeftElement = this.ui.createElement({
            tag: 'div',
            textContent: '相机设置',
            className: 'panel-setting-title-left',
            i18nKey: 'title.cameraSettings'
        });
        const titleRightElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title-right',
        });
        const resetButtonElement = this.ui.createButton({
            textContent: '重置',
            className: ['btn-item', 'btn-text-item', 'inherit-font-size'],
            i18nKey: 'buttons.reset'
        })
        resetButtonElement.addEventListener('click', () => {
            this.onReset();
            this.setActive(false);
        });
        titleRightElement.appendChild(resetButtonElement);

        const closeButtonElement = this.ui.createButton({
            iconName: 'cuowu1',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size'],
            // i18nKey: 'buttons.close'
        })
        closeButtonElement.addEventListener('click', () => {
            this.onClose();
        });
        titleRightElement.appendChild(closeButtonElement);

        titleElement.appendChild(titleLeftElement);
        titleElement.appendChild(titleRightElement);
        panelContainerElement.appendChild(titleElement);

        const viewElement=this.initViewSettingControl();
        panelContainerElement.appendChild(viewElement);

        this.appendChild('panelContainer', panelContainerElement);
        this.setupEventHandlers();
        this.update();
    }
    initViewSettingControl(){
        const viewElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-view',
        });
        const viewTitleElement = this.ui.createElement({
            tag: 'div',
            textContent: '相机参数',
            className: 'panel-setting-view-title',
            i18nKey: 'label.cameraParameters'
        });
        viewElement.appendChild(viewTitleElement);

        const posControlElement = createVector3ControlUI({
            label: '相机位置',
            containerClass: 'panel-setting-view',
            valueOptions: new Map([
                ['x', {id: 'posX', label: 'X', value: globalConfig.cameraParams.position.x, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2)}],
                ['y', {id: 'posY', label: 'Y', value: globalConfig.cameraParams.position.y, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2)}],
                ['z', {id: 'posZ', label: 'Z', value: globalConfig.cameraParams.position.z, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2)}],
            ]),
            isLabelInline:true,
            i18nKey: 'label.cameraPosition'
        });
        this.positionControl = posControlElement;
        // 添加位置变化监听器
        posControlElement.onChange((value: { x: number; y: number; z: number }) => {
            cameraUtils.onPositionChange(value);
        });
        
        viewElement.appendChild(posControlElement.getContainer());

        const viewportViewElement = createViewPortUI({
            containerClass: 'panel-setting-item',
            label: '视口尺寸',
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.viewportSize'
        });
        viewElement.appendChild(viewportViewElement.getContainer());

        const speedElement = createNumericSliderUI({
            id: 'speed-control',
            containerClass:'panel-setting-item',
            label: '相机速度',
            value: globalConfig.cameraParams.speed,
            min: 0.1,
            max: 5.0,
            step: 0.1,
            showValue:false,
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.cameraSpeed'
        });
        this.speedControl = speedElement;
        
        // 添加速度变化监听器
        speedElement.onChange((value: number | null) => {
            if (value !== null) {
                cameraUtils.onSpeedChange(value);
            }
        });
        
        viewElement.appendChild(speedElement.getContainer());

        
        const fovElement = createNumericSliderUI({
            id: 'fov-control',
            containerClass:'panel-setting-item',
            value: globalConfig.cameraParams.fov,
            min: 10,
            max: 170, // 最大视野角超出120度，会导致gizmo显示变形，目前暂时不做修改
            step: 1,
            label: '视野角',
            showValue:false,
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.fov'
        });
        this.fovControl = fovElement;
        
        // 添加FOV变化监听器
        fovElement.onChange((value: number | null) => {
            if (value !== null) {
                cameraUtils.onFOVChange(value);
            }
        });
        
        viewElement.appendChild(fovElement.getContainer());
        
        const nearPlaneElement = new NumericInputUI({
            label: '近平面',
            isLabelInline:false,
            value: globalConfig.cameraParams.nearPlane,
            min: 0.01,
            max: 10,
            step: 0.01,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.nearPlane'
        });
        this.nearPlaneControl = nearPlaneElement;
        
        // 添加近平面变化监听器
        nearPlaneElement.onChange((value: number | null) => {
            if (value !== null) {
                cameraUtils.onNearPlaneChange(value);
            }
        });
        
        viewElement.appendChild(nearPlaneElement.getContainer());

        
        const farPlaneElement = new NumericInputUI({
            label: '远平面',
            isLabelInline:false,
            value: globalConfig.cameraParams.farPlane,
            min: 100,
            max: 10000,
            step: 10,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.farPlane'
        });
        this.farPlaneControl = farPlaneElement;
        
        // 添加远平面变化监听器
        farPlaneElement.onChange((value: number | null) => {
            if (value !== null) {
                cameraUtils.onFarPlaneChange(value);
            }
        });
        
        viewElement.appendChild(farPlaneElement.getContainer());

        return viewElement;
    }

    setupEventHandlers(){
        window.addEventListener('event.panelTabChange', (event: Event) => {
            const detail = (event as CustomEvent).detail;
            console.log('panelTabChange', event,detail);

            this.setActive(detail === 'model');
            this.update();
        });
    }

    /**
     * 设置相机控制器
     */
    public setCameraController(cameraController: CameraController): void {
        this.cameraController = cameraController;
        
        // 延迟更新UI，确保相机控制器完全初始化
        setTimeout(() => {
            this.updateUIFromCamera();
        }, 100);
    }

    /**
     * 从相机更新UI显示
     */
    public updateUIFromCamera(): void {
        if (!this.cameraController || this.isUpdatingFromCamera) {
            return;
        }
        
        this.isUpdatingFromCamera = true;
        
        try {
            const camera = this.cameraController.getCamera();
            if (!camera) {
                return;
            }

            // 更新位置控件
            if (this.positionControl) {
                this.positionControl.setValue({
                    x: camera.position.x,
                    y: camera.position.y,
                    z: camera.position.z
                });
            }

            // 更新FOV控件
            if (this.fovControl) {
                this.fovControl.syncFromInput(camera.fov);
            }

            // 更新近远平面控件
            if (this.nearPlaneControl) {
                this.nearPlaneControl.setValue(camera.near);
            }
            if (this.farPlaneControl) {
                this.farPlaneControl.setValue(camera.far);
            }

            // 更新速度控件
            if (this.speedControl) {
                const moveSpeed = (this.cameraController as any).mouseControls?.moveSpeed || 1.0;
                this.speedControl.syncFromInput(moveSpeed);
            }
        } finally {
            this.isUpdatingFromCamera = false;
        }
    }

    onReset() {
        console.log('重置');
        // 重置到默认值
        if (this.positionControl) {
            this.positionControl.syncFromInput({ x: globalConfig.cameraParams.position.x, y: globalConfig.cameraParams.position.y, z: globalConfig.cameraParams.position.z });
        }
        if (this.fovControl) {
            this.fovControl.syncFromInput(globalConfig.cameraParams.fov);
        }
        if (this.nearPlaneControl) {
            this.nearPlaneControl.syncFromInput(globalConfig.cameraParams.nearPlane);
        }
        if (this.farPlaneControl) {
            this.farPlaneControl.syncFromInput(globalConfig.cameraParams.farPlane);
        }
        if (this.speedControl) {
            this.speedControl.syncFromInput(globalConfig.cameraParams.speed);
        }
        if (this.cameraController) {
            this.cameraController.resetCameraLookAt(0, 0, 0);
        }
    }
    
    onClose() {
        // console.log('关闭');
        globalManager.closeRightToolsBar();
    }
    
    onUpdateCamera(cameraParams: any){
        if (this.positionControl) {
            this.positionControl.syncFromInput({ x: cameraParams.position.x, y: cameraParams.position.y, z: cameraParams.position.z });
        }
        if (this.fovControl) {
            this.fovControl.syncFromInput(cameraParams.fov);
        }
        if (this.nearPlaneControl) {
            this.nearPlaneControl.syncFromInput(cameraParams.near);
        }
        if (this.farPlaneControl) {
            this.farPlaneControl.syncFromInput(cameraParams.far);
        }
        if (this.cameraController) {
            this.cameraController.resetCameraLookAt(0, 0, 0);
        }
    }

    update(isForce: boolean = false){
        if(this.isActive || isForce){
        console.log("相机设置面板更新");
            const cameraController = globalManager.getCurrentCamera();
            if(cameraController){
                this.setCameraController(cameraController);
                this.updateUIFromCamera();
            }
        }
    }
    
    setActive(active: boolean){ 
        this.isActive = active;
    }
}

